/**
 * Раскладка сотрудников по командам — для экрана «Сотрудники» и пикера
 * исполнителей (teams_implementation_plan.md).
 *
 * Правила:
 *   • человек попадает в секцию своей команды (users.team_id);
 *   • Руководитель без своей команды показывается строкой в ПЕРВОЙ команде,
 *     которой руководит (иначе его строка с ролью/телефоном/действиями
 *     потерялась бы в «Без команды» среди тех, кого ещё не разложили);
 *   • в секции Руководитель первым, дальше по имени
 *     (с { seniority: true } — экран «Сотрудники»: по старшинству роли
 *     Суперадмин → Админ → Бригадир → Работник, внутри роли по ФИО);
 *   • остальные — в «Без команды».
 *
 * ⚠️ Сравнение строк — в JS через localeCompare/toLocaleLowerCase: кириллица.
 */

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ru', { numeric: true });

// Старшинство штатных ролей: меньше — выше. Неизвестная роль — в конец.
const ROLE_RANK = { superadmin: 0, admin: 1, foreman: 2, worker: 3 };
const roleRank = (m) => (m.role in ROLE_RANK ? ROLE_RANK[m.role] : 99);
const bySeniority = (a, b) => roleRank(a) - roleRank(b) || byName(a, b);

export function normalizeQuery(q) {
  return String(q || '').trim().toLocaleLowerCase();
}

/** Совпадение сотрудника с поиском: имя, email, телефон. */
export function memberMatches(m, query) {
  if (!query) return true;
  return [m.name, m.email, m.phone]
    .some((v) => v && String(v).toLocaleLowerCase().includes(query));
}

/**
 * @param {Array} members  сотрудники ({ id, name, team_id, ... })
 * @param {Array} teams    команды ({ id, name, lead_user_id, ... }), уже по имени
 * @param {string} query   нормализованный поиск ('' — без фильтра)
 * @returns {{ none: Array<{m, asLead:boolean}>,
 *             sections: Array<{ team, rows: Array<{m, asLead:boolean}> }> }}
 */
export function groupMembersByTeam(members, teams, query = '', { seniority = false } = {}) {
  const teamIds = new Set(teams.map((t) => t.id));
  const leadFirstTeam = new Map();
  for (const t of teams) {
    if (t.lead_user_id && !leadFirstTeam.has(t.lead_user_id)) leadFirstTeam.set(t.lead_user_id, t.id);
  }
  const byTeam = new Map(teams.map((t) => [t.id, []]));
  const none = [];

  for (const m of members) {
    let tid = m.team_id && teamIds.has(m.team_id) ? m.team_id : null;
    let asLead = false;
    if (!tid && leadFirstTeam.has(m.id)) { tid = leadFirstTeam.get(m.id); asLead = true; }
    if (tid) byTeam.get(tid).push({ m, asLead });
    else none.push({ m, asLead: false });
  }

  // Поиск по названию команды показывает её целиком
  const teamHit = (t) => !!query && String(t.name || '').toLocaleLowerCase().includes(query);
  const filterRows = (rows, whole) => (whole ? rows : rows.filter((r) => memberMatches(r.m, query)));

  const sections = teams.map((team) => {
    const rows = filterRows(byTeam.get(team.id), teamHit(team));
    rows.sort((a, b) => {
      if (seniority) return bySeniority(a.m, b.m);
      const al = a.m.id === team.lead_user_id ? 0 : 1;
      const bl = b.m.id === team.lead_user_id ? 0 : 1;
      return al - bl || byName(a.m, b.m);
    });
    return { team, rows };
  }).filter((s) => !query || s.rows.length > 0);

  const noneRows = filterRows(none, false)
    .sort((a, b) => (seniority ? bySeniority(a.m, b.m) : byName(a.m, b.m)));
  return { none: noneRows, sections };
}

/** Какими командами человек руководит (имена) — для подписи в строке. */
export function ledTeamNames(userId, teams) {
  return teams.filter((t) => t.lead_user_id === userId).map((t) => t.name);
}
